import { useEffect } from 'react';
import './SpectralThiefAnimation.css';

/**
 * PROFESSIONAL SPECTRAL THIEF ANIMATION
 * Ghost-type physical move - hides in shadows, steals stat boosts, then attacks
 * 
 * Key Features:
 * - Ghost-type physical attack (90 base power, 100% accuracy)
 * - Steals opponent's stat boosts before attacking
 * - Marshadow signature move with shadow stealth attack
 * - Professional gaming quality with ghostly shadow effects
 */
export default function SpectralThiefAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const elements = [];

    // PHASE 1: Fade Into Shadows (0-500ms)
    playerEl.classList.add('spectral-thief-user-fade');

    const shadowPool = document.createElement('div');
    shadowPool.className = 'spectral-thief-shadow-pool';
    shadowPool.style.left = `${playerX - 50}px`;
    shadowPool.style.top = `${playerY + 15}px`;
    container.appendChild(shadowPool);
    elements.push(shadowPool);

    // Shadow wisps
    for (let i = 0; i < 8; i++) {
      const wisp = document.createElement('div');
      wisp.className = 'spectral-thief-wisp';
      wisp.style.left = `${playerX + (Math.random() - 0.5) * 60 - 6}px`;
      wisp.style.top = `${playerY + (Math.random() - 0.5) * 40 - 6}px`;
      wisp.style.animationDelay = `${i * 50}ms`;
      container.appendChild(wisp);
      elements.push(wisp);
    }

    // PHASE 2: Shadow Travel to Opponent (500-900ms)
    const phase2Timer = setTimeout(() => {
      // Shadow streak traveling along ground
      const dx = opponentX - playerX;
      const dy = opponentY - playerY;
      for (let i = 0; i < 10; i++) {
        const travelTimer = setTimeout(() => {
          const shadow = document.createElement('div');
          shadow.className = 'spectral-thief-travel-shadow';
          const progress = i / 10;
          shadow.style.left = `${playerX + dx * progress + (Math.random() - 0.5) * 15 - 10}px`;
          shadow.style.top = `${playerY + dy * progress + 15 - 5}px`;
          container.appendChild(shadow);
          elements.push(shadow);
        }, i * 35);
        elements.push({ timer: travelTimer, type: 'timer' });
      }

      // Shadow emerging at opponent
      const emergeShadow = document.createElement('div');
      emergeShadow.className = 'spectral-thief-emerge-shadow';
      emergeShadow.style.left = `${opponentX - 50}px`;
      emergeShadow.style.top = `${opponentY + 15}px`;
      emergeShadow.style.animationDelay = '250ms';
      container.appendChild(emergeShadow);
      elements.push(emergeShadow);
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Spectral Strike (900-1400ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('spectral-thief-opponent-hit');

      // Ghost slash marks
      for (let i = 0; i < 3; i++) {
        const slashTimer = setTimeout(() => {
          const slash = document.createElement('div');
          slash.className = 'spectral-thief-slash';
          slash.style.left = `${opponentX - 30 + i * 15}px`;
          slash.style.top = `${opponentY - 40 + i * 10}px`;
          slash.style.transform = `rotate(${-30 + i * 15}deg)`;
          container.appendChild(slash);
          elements.push(slash);
        }, i * 60);
        elements.push({ timer: slashTimer, type: 'timer' });
      }

      // Ghost impact
      const impact = document.createElement('div');
      impact.className = 'spectral-thief-impact';
      impact.style.left = `${opponentX - 90}px`;
      impact.style.top = `${opponentY - 90}px`;
      container.appendChild(impact);
      elements.push(impact);

      // Shadow burst particles
      for (let i = 0; i < 16; i++) {
        const burstAngle = (i / 16) * 360;
        const burstDist = 45 + Math.random() * 40;
        const burst = document.createElement('div');
        burst.className = 'spectral-thief-burst';
        burst.style.left = `${opponentX - 5}px`;
        burst.style.top = `${opponentY - 5}px`;
        burst.style.setProperty('--scatter-x', `${Math.cos(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.setProperty('--scatter-y', `${Math.sin(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.animationDelay = `${i * 15}ms`;
        container.appendChild(burst);
        elements.push(burst);
      }

      // Shockwaves
      for (let i = 0; i < 2; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'spectral-thief-shockwave';
          sw.style.left = `${opponentX - 60}px`;
          sw.style.top = `${opponentY - 60}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }
    }, 900);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Return from Shadows (1400-1700ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('spectral-thief-user-fade');
      playerEl.classList.add('spectral-thief-user-return');
    }, 1400);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1700ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('spectral-thief-user-fade', 'spectral-thief-user-return');
      opponentEl.classList.remove('spectral-thief-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1700);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('spectral-thief-user-fade', 'spectral-thief-user-return');
      opponentEl.classList.remove('spectral-thief-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

SpectralThiefAnimation.moveType = 'ghost';
